import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Paper,
  Stack
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { apiService } from '../../services/apiService';
import { ProductFormData } from '../../types';

// Función para parsear números con diferentes formatos
const parseNumberFrontend = (value: any): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  const valueStr = String(value).trim();
  if (!valueStr) return null;
  
  // Manejar diferentes formatos de decimales
  let cleaned = valueStr;
  
  // Si tiene tanto coma como punto, determinar cuál es el separador decimal
  if (cleaned.includes(',') && cleaned.includes('.')) {
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    
    if (lastComma > lastDot) {
      // Formato europeo: 1.234,56
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato US: 1,234.56
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (cleaned.includes(',')) {
    // Solo coma - asumir que es separador decimal
    cleaned = cleaned.replace(',', '.');
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
};

// Función de validación personalizada para dimensiones
const validateDimension = (value: any, fieldName: string) => {
  const parsed = parseNumberFrontend(value);
  
  if (parsed === null) return true; // Es opcional
  
  if (parsed < 0) {
    return `${fieldName} debe ser positivo`;
  }
  
  if (parsed > 9999999) {
    return `${fieldName} no puede exceder 9,999,999 metros`;
  }
  
  // Validar decimales (máximo 3)
  const str = String(parsed);
  const decimalPart = str.split('.')[1];
  if (decimalPart && decimalPart.length > 3) {
    return `${fieldName} puede tener máximo 3 decimales`;
  }
  
  return true;
};

// Esquema de validación actualizado para la nueva estructura.
//
// CLAUDE.md §4 (2026-09-02): precio_unitario/currency/descuento ya no viven
// en Product - el precio de Materiales se gestiona en Listas de Precios
// (PriceListManagement.tsx). `stock` tampoco se edita acá (tiene su propia
// tabla, CLAUDE.md §7) - ver la sección "Stock" más abajo.
// `factor_conversion_m2` (CLAUDE.md §7) SÍ se edita acá - pese al nombre no
// es un precio, es el factor de conversión unidad->m2.
const productSchema = yup.object().shape({
  sku: yup.string(),
  codigo: yup.string().required('El código es requerido'),
  color: yup.string(),
  grupo: yup.string(),
  categoria: yup.string(),
  descripcion: yup.string(),
  proveedor: yup.string(),
  unidad: yup.string(),
  peso: yup.number().min(0, 'El peso no puede ser negativo').nullable(),
  moq_m2: yup.number().min(0, 'El MOQ no puede ser negativo').nullable(),
  largo: yup.mixed().test('valid-dimension', 'Dimensión inválida', function(value) {
    const result = validateDimension(value, 'El largo');
    return result === true ? true : this.createError({ message: result });
  }),
  ancho: yup.mixed().test('valid-dimension', 'Dimensión inválida', function(value) {
    const result = validateDimension(value, 'El ancho');
    return result === true ? true : this.createError({ message: result });
  }),
  espesor: yup.mixed().test('valid-dimension', 'Dimensión inválida', function(value) {
    const result = validateDimension(value, 'El espesor');
    return result === true ? true : this.createError({ message: result });
  }),
  factor_conversion_m2: yup.number().min(0, 'El factor de conversión m² debe ser positivo'),
  estructura_m2: yup.string().test(
    'valid-structure',
    'El formato debe ser: codigo,cantidad (uno por línea)',
    function(value) {
      if (!value) return true; // Es opcional
      
      const lines = value.split('\n').filter(line => line.trim());
      return lines.every(line => {
        const parts = line.trim().split(',');
        return parts.length === 2 && 
               parts[0].trim() && 
               !isNaN(Number(parts[1].trim())) && 
               Number(parts[1].trim()) > 0;
      });
    }
  ),
  is_active: yup.boolean(),
  is_outlet: yup.boolean(),
});

// Inferir el tipo del esquema (no lo usamos pero lo dejamos para referencia futura)

export const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Foto del producto (Phase 5, CLAUDE.md §7) - acción separada del guardado
  // del formulario, ya que sube a Cloud Storage por su propio endpoint.
  const [fotoUrl, setFotoUrl] = useState<string | undefined>(undefined);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Stock (CLAUDE.md §7) - vive en su propia tabla, se guarda con su propio
  // endpoint (PUT /products/{id}/stock), igual que la foto.
  const [stock, setStock] = useState<number | ''>('');
  const [savingStock, setSavingStock] = useState(false);
  const [stockError, setStockError] = useState<string | null>(null);
  const [stockSaved, setStockSaved] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm<ProductFormData>({
    // Usar validación manual mejorada
    defaultValues: {
      sku: '',
      codigo: '',
      color: '',
      grupo: '',
      categoria: '',
      descripcion: '',
      proveedor: '',
      unidad: '',
      peso: undefined,
      moq_m2: undefined,
      largo: undefined,
      ancho: undefined,
      espesor: undefined,
      factor_conversion_m2: undefined,
      estructura_m2: '',
      is_active: true,
      is_outlet: false
    }
  });

  useEffect(() => {
    if (isEdit && id) {
      loadProduct(parseInt(id));
    }
  }, [id, isEdit]);

  const loadProduct = async (productId: number) => {
    try {
      setLoading(true);
      setServerError(null);

      const productData = await apiService.products.getById(productId);

      reset({
        codigo: productData.codigo,
        sku: productData.sku || '',
        color: productData.color || '',
        grupo: productData.grupo || '',
        categoria: productData.categoria || '',
        descripcion: productData.descripcion || '',
        proveedor: productData.proveedor || '',
        unidad: productData.unidad || '',
        peso: productData.peso,
        moq_m2: productData.moq_m2,
        largo: productData.largo,
        ancho: productData.ancho,
        espesor: productData.espesor,
        factor_conversion_m2: productData.factor_conversion_m2,
        estructura_m2: productData.estructura_m2 || '',
        is_active: productData.is_active,
        is_outlet: productData.is_outlet || false
      } as any);
      setFotoUrl(productData.foto_url);
      setStock(productData.stock ?? '');
    } catch (err: any) {
      console.error('Error loading product:', err);
      setServerError('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setServerError(null);
      clearErrors();
      
      // Validar y parsear dimensiones
      const largoValidation = validateDimension(data.largo, 'El largo');
      if (largoValidation !== true) {
        setError('largo', { message: largoValidation });
        return;
      }
      
      const anchoValidation = validateDimension(data.ancho, 'El ancho');
      if (anchoValidation !== true) {
        setError('ancho', { message: anchoValidation });
        return;
      }
      
      const espesorValidation = validateDimension(data.espesor, 'El espesor');
      if (espesorValidation !== true) {
        setError('espesor', { message: espesorValidation });
        return;
      }
      
      const apiData: ProductFormData = {
        sku: data.sku,
        codigo: data.codigo,
        color: data.color,
        grupo: data.grupo,
        categoria: data.categoria,
        descripcion: data.descripcion,
        proveedor: data.proveedor,
        unidad: data.unidad,
        peso: data.peso,
        moq_m2: data.moq_m2,
        largo: parseNumberFrontend(data.largo) || undefined,
        ancho: parseNumberFrontend(data.ancho) || undefined,
        espesor: parseNumberFrontend(data.espesor) || undefined,
        factor_conversion_m2: data.factor_conversion_m2,
        estructura_m2: data.estructura_m2,
        is_active: data.is_active,
        is_outlet: data.is_outlet
      };

      if (isEdit && id) {
        await apiService.products.update(parseInt(id), apiData);
      } else {
        await apiService.products.create(apiData);
      }

      navigate('/products');
    } catch (err: any) {
      console.error('Error saving product:', err);
      setServerError(err.response?.data?.detail || 'Error al guardar el producto');
    }
  };

  // Stock (CLAUDE.md §7) - guardado por separado, mismo patrón que la foto.
  const handleStockSave = async () => {
    if (!id || stock === '') return;
    try {
      setSavingStock(true);
      setStockError(null);
      await apiService.products.updateStock(parseInt(id), Number(stock));
      setStockSaved(true);
      setTimeout(() => setStockSaved(false), 3000);
    } catch (err: any) {
      setStockError(err.response?.data?.detail || 'Error al guardar el stock');
    } finally {
      setSavingStock(false);
    }
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!file || !id) return;
    try {
      setUploadingPhoto(true);
      setPhotoError(null);
      const updated = await apiService.products.uploadPhoto(parseInt(id), file);
      setFotoUrl(updated.foto_url);
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Producto' : 'Nuevo Producto'}
        </Typography>
      </Box>

      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {serverError}
        </Alert>
      )}

      {isEdit && (
        <Paper sx={{ p: 3, borderRadius: 4, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Foto del producto
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 96, height: 96, borderRadius: 2, overflow: 'hidden',
                bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid', borderColor: 'divider', flexShrink: 0,
              }}
            >
              {fotoUrl ? (
                <img src={fotoUrl} alt="Foto del producto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Typography variant="caption" color="text.secondary">Sin foto</Typography>
              )}
            </Box>
            <Box>
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={uploadingPhoto}
                startIcon={uploadingPhoto ? <CircularProgress size={16} /> : undefined}
              >
                {fotoUrl ? 'Reemplazar foto' : 'Subir foto'}
                <input type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handlePhotoChange} />
              </Button>
              {photoError && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  {photoError}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {isEdit && (
        <Paper sx={{ p: 3, borderRadius: 4, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Stock
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            El stock vive en su propia tabla (CLAUDE.md §7) - se guarda por separado, no con el resto del formulario.
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              label="Cantidad"
              type="number"
              size="small"
              value={stock}
              onChange={(e) => setStock(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ maxWidth: 160 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleStockSave}
              disabled={savingStock || stock === ''}
              startIcon={savingStock ? <CircularProgress size={16} /> : undefined}
            >
              Guardar stock
            </Button>
            {stockSaved && <Alert severity="success" sx={{ py: 0 }}>Guardado</Alert>}
          </Box>
          {stockError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              {stockError}
            </Typography>
          )}
        </Paper>
      )}

  <Paper sx={{ p: 3, borderRadius: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <Controller
              name="codigo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Código *"
                  error={!!errors.codigo}
                  helperText={errors.codigo?.message}
                />
              )}
            />

            <Controller
              name="sku"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="SKU"
                />
              )}
            />

            <Box display="flex" gap={2}>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Color"
                  />
                )}
              />

              <Controller
                name="grupo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Grupo"
                  />
                )}
              />

              <Controller
                name="categoria"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Categoría"
                  />
                )}
              />
            </Box>

            <Box display="flex" gap={2}>
              <Controller
                name="proveedor"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Proveedor"
                  />
                )}
              />

              <Controller
                name="unidad"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Unidad"
                    helperText="Unidad de medida del producto (ej: m2, unidad, rollo)"
                  />
                )}
              />
            </Box>

            <Box display="flex" gap={2}>
              <Controller
                name="peso"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label="Peso (kg)"
                    type="number"
                    inputProps={{ step: "0.001", min: "0" }}
                    error={!!errors.peso}
                    helperText={errors.peso?.message || "Opcional - no todos los productos tienen peso"}
                  />
                )}
              />

              <Controller
                name="moq_m2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label="MOQ (m²)"
                    type="number"
                    inputProps={{ step: "0.001", min: "0" }}
                    error={!!errors.moq_m2}
                    helperText={errors.moq_m2?.message || "Cantidad mínima de pedido, en m²"}
                  />
                )}
              />
            </Box>

            {/* Sección de Dimensiones */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Dimensiones (en metros)
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Formato de números:</strong> Puedes usar punto (.) o coma (,) como separador decimal.
              Ejemplos válidos: 3.66, 3,66, 1.234,56, 1,234.56. Máximo 3 decimales y 9,999,999 metros.
            </Alert>
            
            <Box display="flex" gap={2}>
              <Controller
                name="largo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Largo (m)"
                    type="text"
                    placeholder="3.66 o 3,66"
                    error={!!errors.largo}
                    helperText={errors.largo?.message || "Longitud en metros (ej: 3.66 o 3,66)"}
                  />
                )}
              />

              <Controller
                name="ancho"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Ancho (m)"
                    type="text"
                    placeholder="0.138 o 0,138"
                    error={!!errors.ancho}
                    helperText={errors.ancho?.message || "Anchura en metros (ej: 0.138 o 0,138)"}
                  />
                )}
              />

              <Controller
                name="espesor"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Espesor (m)"
                    type="text"
                    placeholder="0.02 o 0,02"
                    error={!!errors.espesor}
                    helperText={errors.espesor?.message || "Grosor en metros (ej: 0.02 o 0,02)"}
                  />
                )}
              />
            </Box>

            {/* NOTA (CLAUDE.md §4): el precio de Materiales (UNITARIO/
                DESCUENTO) ya no se edita acá - vive en Listas de Precios
                (por producto y por lista, ver PriceListManagement.tsx).
                `factor_conversion_m2` no es un precio pese al nombre - es el factor de
                conversión unidad->m² (CONVERSIONM2 en la planilla real,
                CLAUDE.md §7). */}
            <Alert severity="info" sx={{ mb: 1 }}>
              El precio de este producto se gestiona en <strong>Listas de Precios</strong>, no acá.
            </Alert>
            <Controller
              name="factor_conversion_m2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  fullWidth
                  label="Factor de conversión m²"
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  helperText="Factor para convertir la UNIDAD del producto a m² (no es un precio)"
                />
              )}
            />

            {/* Sección de Estructura */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Estructura por M²
            </Typography>
            
            <Controller
              name="estructura_m2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Lista de productos para estructura"
                  multiline
                  rows={4}
                  placeholder="Formato: codigo,cantidad (uno por línea)
Ejemplo:
PGC80X0.90,1
COBRA21,21"
                  error={!!errors.estructura_m2}
                  helperText={errors.estructura_m2?.message || "Un producto por línea con formato: codigo,cantidad"}
                />
              )}
            />

            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Producto activo"
                />
              )}
            />

            <Controller
              name="is_outlet"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Outlet"
                />
              )}
            />

            <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear')}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
